/* =============================================================================
   mbi-quick-view.js — MBI enhancements for Boost's native Quick View modal
   Version: 2026-10-05.3
   Loaded from snippets/mbi-css-assets.liquid (app-overrides group) ONLY on
   page types that render Boost cards: index, collection, search, product,
   cart. Styling: assets/mbi-quick-view.css (same gate, non-blocking).

   Rev 3: description fetch starts on the eye click (arrives with the modal),
   description + link moved above the purchase block (DOM, so tab order
   matches), sale/regular price labels for screen readers, info column taken
   out of the tab order, and Add to Basket uses the AJAX cart + Align drawer
   like the cards instead of Boost's redirect to /cart.

   Goal: the modal reads as a larger MBI product card.
     - copies the source card's MBI badge stack (Sale / New / Sold out…) onto
       the main image, and its content pills (Save £x, Best Seller…) under the
       title — the card is the single source of truth, so labels always match
     - removes the trailing ISO currency code ("£14.99 GBP" → "£14.99")
     - PDP quantity icons (basket icon is a CSS ::before in MAB)
     - short description (from /products/<handle>.js, market-aware), clamped
       to 3 lines with an ellipsis by CSS
     - "View full details" keeps the visitor's market path (/en-us/…)

   MARKET COMMERCE (three-way rule, matches cards + PDP):
     ecommerce ON            → Boost's Shopify controls, restyled
     ecommerce OFF + Amazon  → "Available at Amazon" badge (URL from the card's
                               own Amazon CTA, which the Boost pair resolved
                               from product.metafields.amazon.<country>)
     ecommerce OFF, no link  → catalogue-only copy (MBIMarket.catalogueText)
   Shopify controls are already removed in OFF markets by customization.js
   and hidden first-paint by mbi-apps-boost.css; this file never re-adds them.

   No Boost API used (G1): Boost renders the modal into #boost-sd__modal-quickview
   in the light DOM; we observe that node only while it exists.
   ========================================================================== */
(() => {
  'use strict';

  if (window.__mbiQuickViewLoaded) return;
  window.__mbiQuickViewLoaded = true;

  const VERSION = '2026-10-06.1';
  const MODAL_ID = 'boost-sd__modal-quickview';
  const market = window.MBIMarket || {};
  const ECOMMERCE_ON = !document.documentElement.classList.contains('mbi-ecommerce-off');
  const ROUTES_ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const DESC_CHARS = 320; // text kept before CSS clamps to 3 lines

  /* PDP quantity icons (snippets/icon minus/plus as rendered on the PDP). */
  const ICON_MINUS =
    '<svg width="15" height="4" viewBox="0 0 15 4" fill="currentColor" aria-hidden="true" focusable="false"><path d="M1 3.33226C0.447715 3.33226 0 2.88455 0 2.33226V1.66779C0 1.1155 0.447715 0.667786 1 0.667786H14C14.5523 0.667786 15 1.1155 15 1.66779V2.33226C15 2.88455 14.5523 3.33226 14 3.33226H1Z"/></svg>';
  const ICON_PLUS =
    '<svg width="15" height="16" viewBox="0 0 15 16" fill="currentColor" aria-hidden="true" focusable="false"><path d="M7.18421 15.5C6.63193 15.5 6.18421 15.0523 6.18421 14.5V1.5C6.18421 0.947715 6.63193 0.5 7.18421 0.5H7.84868C8.40097 0.5 8.84868 0.947715 8.84868 1.5V14.5C8.84868 15.0523 8.40097 15.5 7.84868 15.5H7.18421ZM1 9.34869C0.447715 9.34869 0 8.90097 0 8.34869V7.68421C0 7.13193 0.447715 6.68421 1 6.68421H14C14.5523 6.68421 15 7.13193 15 7.68421V8.34869C15 8.90097 14.5523 9.34869 14 9.34869H1Z"/></svg>';

  const descriptionCache = new Map(); // handle → Promise<string>
  const noDescription = new Set(); // handles with no description: never re-insert a placeholder
  let sourceCard = null;
  let modalObserver = null;
  let scheduled = false;
  let openingUntil = 0; // repeat-click guard window (ms timestamp)

  /* ---------- helpers ---------------------------------------------------- */

  function marketPath(href) {
    // Boost writes "/products/…"; keep the visitor inside their market path.
    if (!href || ROUTES_ROOT === '/' || !href.startsWith('/products/')) return href;
    return ROUTES_ROOT.replace(/\/$/, '') + href;
  }

  function handleFromHref(href) {
    const match = /\/products\/([^/?#]+)/.exec(href || '');
    return match ? decodeURIComponent(match[1]) : '';
  }

  function findSourceCard(productId) {
    if (
      sourceCard &&
      sourceCard.isConnected &&
      (sourceCard.dataset.productId === productId || sourceCard.id === productId)
    ) {
      return sourceCard;
    }
    // Fallback (e.g. keyboard/other trigger): first visible card for this product.
    const cards = document.querySelectorAll(
      `.boost-sd__product-item[data-product-id="${CSS.escape(productId)}"], .boost-sd__product-item[id="${CSS.escape(productId)}"]`
    );
    return Array.from(cards).find((card) => card.offsetParent !== null) || cards[0] || null;
  }

  function fetchDescription(handle) {
    if (!handle) return Promise.resolve('');
    if (!descriptionCache.has(handle)) {
      const url = `${ROUTES_ROOT}products/${encodeURIComponent(handle)}.js`;
      descriptionCache.set(
        handle,
        fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
          .then((response) => (response.ok ? response.json() : null))
          .then((product) => {
            if (!product || !product.description) return '';
            const doc = new DOMParser().parseFromString(product.description, 'text/html');
            doc.querySelectorAll('script, style, iframe').forEach((node) => node.remove());
            const text = (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
            return text.length > DESC_CHARS ? `${text.slice(0, DESC_CHARS).replace(/\s+\S*$/, '')}…` : text;
          })
          .catch(() => '')
      );
    }
    return descriptionCache.get(handle);
  }

  function handleFromCard(card) {
    // Boost cards carry data-product JSON with the handle; the link is the fallback.
    if (!card) return '';
    try {
      const data = JSON.parse(card.getAttribute('data-product') || '{}');
      if (data.handle) return data.handle;
    } catch (_error) {
      /* fall through to the link */
    }
    const link = card.querySelector('a.boost-sd__product-link[href*="/products/"]');
    return link ? handleFromHref(link.getAttribute('href')) : '';
  }

  function cartAddUrl() {
    // Same endpoint the card buttons use (customization.js addBoostProductToThemeCart).
    return (window.routes && window.routes.cartAdd) || `${ROUTES_ROOT}cart/add.js`;
  }

  function cartUrl() {
    return (window.routes && window.routes.cart) || `${ROUTES_ROOT}cart`;
  }

  /* ---------- enhancement passes (all idempotent) ------------------------ */

  function fixFocusStops(info) {
    // Boost's tabindex="0" on the info column = a non-interactive tab stop.
    if (info.getAttribute('tabindex') === '0') info.setAttribute('tabindex', '-1');
  }

  function labelPrices(info) {
    // Boost's sale price markup reads "£5.99£7.99" to screen readers.
    const price = info.querySelector('.boost-sd__quick-view-price');
    const compare = price && price.querySelector('.boost-sd__format-currency--price-compare');
    if (!price || !compare || price.querySelector('.mbi-qv-sr')) return;
    const sale = Array.from(price.children).find(
      (node) => node.classList.contains('boost-sd__format-currency') && node !== compare
    );
    const label = (text) => {
      const span = document.createElement('span');
      span.className = 'visually-hidden mbi-qv-sr';
      span.textContent = text;
      return span;
    };
    if (sale) sale.before(label('Sale price'));
    compare.before(label('Regular price'));
  }

  function stripCurrencyCodes(info) {
    // "£14.99 GBP" → "£14.99". Leaf spans only, write only when changed so
    // the modal observer settles immediately.
    info.querySelectorAll('.boost-sd__quick-view-price .boost-sd__format-currency').forEach((node) => {
      if (node.children.length) return;
      const clean = node.textContent.replace(/\s+[A-Z]{3}\s*$/, '');
      if (clean !== node.textContent) node.textContent = clean;
    });
  }

  function copyCardLabels(quickView, info, card) {
    const title = info.querySelector('.boost-sd__quick-view-title');

    // Content pills (Save £x, Best Seller, Selling Fast…) under the title.
    const cardPills = card && card.querySelector('.mbi-card-pill-group');
    let pills = info.querySelector('.mbi-qv-pills');
    if (cardPills && cardPills.children.length) {
      const signature = cardPills.getAttribute('data-mbi-pill-signature') || cardPills.textContent;
      if (!pills || pills.dataset.signature !== signature) {
        if (pills) pills.remove();
        pills = cardPills.cloneNode(true);
        pills.classList.add('mbi-qv-pills');
        pills.dataset.signature = signature;
        pills.removeAttribute('data-mbi-pill-signature');
        if (title) title.insertAdjacentElement('afterend', pills);
      }
    } else if (pills) {
      pills.remove();
    }

    // Image badges (Sale / New / Bundle / Sold out) on the main image.
    const cardStack = card && card.querySelector('.mbi-badge-stack');
    const imageHost = quickView.querySelector('.boost-sd__quick-view-product-image .slick-slider') ||
      quickView.querySelector('.boost-sd__quick-view-product-image');
    let stack = quickView.querySelector('.mbi-qv-badges');
    const hasBadges = cardStack && cardStack.querySelector('.mbi-badge:not(.mbi-badge--empty)');
    if (hasBadges && imageHost) {
      const signature = cardStack.textContent.replace(/\s+/g, ' ').trim();
      if (!stack || stack.dataset.signature !== signature || stack.parentElement !== imageHost) {
        if (stack) stack.remove();
        stack = cardStack.cloneNode(true);
        stack.removeAttribute('style');
        stack.classList.add('mbi-qv-badges');
        stack.dataset.signature = signature;
        stack.setAttribute('aria-hidden', 'true'); // labels are decorative copies; price/pills carry meaning
        imageHost.appendChild(stack);
      }
    } else if (stack) {
      stack.remove();
    }
  }

  function applySoldOutState(info) {
    /* Sold-out product in an ecommerce market (Quick View is now reachable
       from sold-out cards): Boost disables Add to Basket but labels it
       "View Product" and leaves Buy it now active, which would fail at
       checkout. Show a clear Sold out state and remove the buy routes. */
    const cart = info.querySelector('.boost-sd__btn-add-to-cart');
    if (!cart) return;
    const soldOut = cart.disabled || cart.getAttribute('aria-disabled') === 'true';
    info.classList.toggle('mbi-qv-sold-out', soldOut);
    if (!soldOut) return;
    const label = cart.querySelector('.boost-sd__button-text') || cart;
    if (label.textContent.trim() !== 'Sold out') label.textContent = 'Sold out';
    cart.setAttribute('aria-label', 'Sold out');
  }

  function decorateQuantity(info) {
    info.querySelectorAll('.boost-sd__input-number-btn').forEach((button) => {
      if (button.dataset.mbiIcon) return;
      const isMinus = /decrease/i.test(button.getAttribute('aria-label') || '');
      const span = button.querySelector('span') || button;
      span.innerHTML = isMinus ? ICON_MINUS : ICON_PLUS;
      button.dataset.mbiIcon = 'true';
    });
  }

  function applyMarketCta(info, card) {
    if (ECOMMERCE_ON) return;
    const host = info.querySelector('.boost-sd__quick-view-btn');
    if (!host) return;

    const amazonUrl = card && card.querySelector('.mbi-boost-amazon-cta')
      ? card.querySelector('.mbi-boost-amazon-cta').getAttribute('href')
      : '';
    const existing = host.querySelector('.mbi-qv-market');
    const state = amazonUrl ? `amazon:${amazonUrl}` : 'catalogue';
    if (existing && existing.dataset.state === state) return;
    if (existing) existing.remove();

    const wrap = document.createElement('div');
    wrap.className = 'mbi-qv-market';
    wrap.dataset.state = state;

    if (amazonUrl && market.amazonBadgeUrl) {
      const title = (info.querySelector('.boost-sd__quick-view-title') || {}).textContent || 'this title';
      const link = document.createElement('a');
      link.className = 'mbi-amazon-button mbi-amazon-button--badge';
      link.href = amazonUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer sponsored';
      link.setAttribute(
        'aria-label',
        `View ${title.trim()} on Amazon${market.countryName ? ` ${market.countryName}` : ''} (opens in a new tab)`
      );
      const img = document.createElement('img');
      img.className = 'mbi-amazon-button__badge-image';
      img.src = market.amazonBadgeUrl;
      img.width = 230;
      img.height = 126;
      img.alt = 'Available at Amazon';
      link.appendChild(img);
      wrap.appendChild(link);
    } else {
      // Restrained catalogue treatment — same copy and classes as the PDP.
      const note = document.createElement('div');
      note.className = 'mbi-market-availability mbi-market-availability--compact figtree_para';
      note.setAttribute('role', 'status');
      const p = document.createElement('p');
      p.className = 'mbi-market-availability__compact';
      p.textContent = market.catalogueText || 'Available through selected retailers';
      note.appendChild(p);
      wrap.appendChild(note);
    }
    host.appendChild(wrap);
    host.style.display = 'block';
  }

  function applyDescriptionAndLink(info) {
    const link = info.querySelector('.boost-sd__quick-view-details-link');
    if (!link) return;

    const fixed = marketPath(link.getAttribute('href'));
    if (fixed !== link.getAttribute('href')) link.setAttribute('href', fixed);

    /* Reading content before the purchase block, in the DOM (not just CSS
       order) so Tab / screen-reader order matches what is seen:
       … stars → description → View full details → quantity → buttons. */
    const purchaseStart = info.querySelector(
      '.boost-sd__quick-view-quantity, .boost-sd__quick-view-preorder, .boost-sd__quick-view-btn'
    );

    const handle = handleFromHref(fixed);
    let desc = info.querySelector('.mbi-qv-desc');
    if (noDescription.has(handle)) {
      if (desc) desc.remove();
      if (purchaseStart && link.nextElementSibling !== purchaseStart) purchaseStart.before(link);
      return;
    }
    if (!desc || desc.dataset.handle !== handle) {
      if (desc) desc.remove();
      // Placeholder reserves the 3 lines up front so nothing jumps when it fills.
      desc = document.createElement('p');
      desc.className = 'mbi-qv-desc mbi-qv-desc--loading';
      desc.dataset.handle = handle;
    }

    if (purchaseStart) {
      if (desc.nextElementSibling !== link || link.nextElementSibling !== purchaseStart) {
        purchaseStart.before(desc, link);
      }
    } else if (desc.nextElementSibling !== link) {
      link.before(desc);
    }

    if (!desc.classList.contains('mbi-qv-desc--loading') || desc.dataset.requested) return;
    desc.dataset.requested = 'true';

    fetchDescription(handle).then((text) => {
      if (!desc.isConnected || desc.dataset.handle !== handle) return;
      if (!text) {
        noDescription.add(handle);
        desc.remove();
        return;
      }
      desc.textContent = text;
      desc.classList.remove('mbi-qv-desc--loading');
    });
  }

  function enhance() {
    scheduled = false;
    const modal = document.getElementById(MODAL_ID);
    if (!modal) return;
    const quickView = modal.querySelector('.boost-sd__quick-view');
    const info = modal.querySelector('.boost-sd__quick-view-product-info');
    if (!quickView || !info) return;

    const productId = quickView.getAttribute('product-id') || '';
    const card = productId ? findSourceCard(productId) : null;

    modal.classList.add('mbi-qv');
    fixFocusStops(info);
    stripCurrencyCodes(info);
    labelPrices(info);
    copyCardLabels(quickView, info, card);
    if (ECOMMERCE_ON) {
      decorateQuantity(info);
    }
    if (ECOMMERCE_ON) applySoldOutState(info);
    applyMarketCta(info, card);
    applyDescriptionAndLink(info);
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(enhance);
  }

  function observeModal() {
    const modal = document.getElementById(MODAL_ID);
    if (!modal) return false;
    openingUntil = 0;
    if (!modalObserver || modalObserver.target !== modal) {
      if (modalObserver) modalObserver.disconnect();
      modalObserver = new MutationObserver(schedule);
      // href included: Boost re-writes the details link on re-render.
      modalObserver.observe(modal, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['href'],
      });
      modalObserver.target = modal;
    }
    schedule();
    return true;
  }

  /* Boost creates the modal on first open (after its data fetch), so wait for
     it briefly after any Quick View click, then rely on the modal observer. */
  function waitForModal() {
    if (observeModal()) return;
    const started = performance.now();
    const bodyObserver = new MutationObserver(() => {
      if (observeModal() || performance.now() - started > 8000) bodyObserver.disconnect();
    });
    bodyObserver.observe(document.body, { childList: true });
    setTimeout(() => bodyObserver.disconnect(), 8000);
  }

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest &&
        event.target.closest('.boost-sd__button--quick-view, .boost-sd__btn-quick-view');
      if (!trigger) return;

      /* Boost bug: a second click before its modal appears (double-click, or
         an impatient re-tap while the product loads) builds a SECOND modal
         with the same id on top of the first. Swallow repeat clicks while one
         is opening or open — capture phase on document runs before Boost's
         delegated handler. */
      const now = performance.now();
      const openModal = document.getElementById(MODAL_ID);
      const modalShowing = Boolean(openModal && openModal.getClientRects().length);
      if (modalShowing || now < openingUntil) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      openingUntil = now + 2500;

      sourceCard = trigger.closest('.boost-sd__product-item');
      // Start the description now, in parallel with Boost's own product fetch,
      // so it is usually ready when the modal paints (no placeholder wait).
      fetchDescription(handleFromCard(sourceCard));
      waitForModal();
    },
    true
  );

  /* ---------- Add to Basket: AJAX cart + Align drawer (ecommerce ON) -------
     Boost's quick-view handler redirects to /cart. Cards already avoid that
     (customization.js bindThemeCartDrawerToBoostButton); do the same here.
     The POST goes through window.fetch, which customization.js wraps: on a
     successful /cart/add it refreshes the header count and opens the Align
     mini-cart. We close the modal first so the drawer is visible.
     If that watcher isn't present (e.g. Boost JS failed to load), fall back to
     Boost's original behaviour: go to the basket page. */
  if (ECOMMERCE_ON) {
    document.addEventListener(
      'click',
      (event) => {
        const button = event.target.closest &&
          event.target.closest(`#${MODAL_ID} .boost-sd__btn-add-to-cart`);
        if (!button) return;
        const variantId = button.getAttribute('data-variant-id');
        if (!variantId) return; // let Boost handle anything unexpected

        event.preventDefault();
        event.stopImmediatePropagation();
        if (button.getAttribute('aria-busy') === 'true') return;

        const modal = button.closest(`#${MODAL_ID}`);
        const input = modal && modal.querySelector('.boost-sd__input-number-input');
        const quantity = Math.max(1, parseInt(input && input.value, 10) || 1);
        const oldError = modal && modal.querySelector('.mbi-qv-error');
        if (oldError) oldError.remove();

        button.setAttribute('aria-busy', 'true');
        button.disabled = true;

        fetch(cartAddUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ items: [{ id: variantId, quantity }] }),
        })
          .then((response) =>
            response.json().catch(() => ({})).then((result) => ({ response, result }))
          )
          .then(({ response, result }) => {
            if (!response.ok || result.status) {
              throw new Error(result.description || result.message || 'Sorry, this could not be added to your basket.');
            }
            document.body.classList.add('added-to-cart');
            if (!window.mbiBoostCartAddWatcherInstalled) {
              window.location.href = cartUrl();
              return;
            }
            const close = modal && modal.querySelector('.boost-sd__modal-close-btn');
            if (close) close.click();
          })
          .catch((error) => {
            // Shopify's message, e.g. "You can only add 3 of this item to your basket."
            const host = modal && modal.querySelector('.boost-sd__quick-view-product-info');
            if (host) {
              const message = document.createElement('p');
              message.className = 'mbi-qv-error';
              message.setAttribute('role', 'alert');
              message.textContent = error.message;
              host.appendChild(message);
            }
          })
          .finally(() => {
            button.removeAttribute('aria-busy');
            button.disabled = false;
          });
      },
      true
    );
  }

  window.mbiQuickViewDebug = () => ({
    version: VERSION,
    ecommerceOn: ECOMMERCE_ON,
    routesRoot: ROUTES_ROOT,
    modal: Boolean(document.getElementById(MODAL_ID)),
    observing: Boolean(modalObserver),
    cachedDescriptions: descriptionCache.size,
  });

  observeModal();
})();
